import React, { useState, useEffect } from 'react';
import {
  Loader2, AlertCircle, Heart, TrendingUp, Calculator,
  MapPin, Home, DollarSign, Search, Bot, Shield, Building2, Award,
  CheckCircle, XCircle, Gauge, ExternalLink, Briefcase,
} from 'lucide-react';
import { getContactProfileSummary } from '../../../api/contact';

const TEAL = '#55C5D0';

function fmtMoney(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);
}

function fmtDate(s) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Generic card shell ──────────────────────────────────────────────────────
function Card({ icon: Icon, title, accent = TEAL, children, badge }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}1a` }}>
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
        <h3 className="font-bold text-gray-900 text-sm">{title}</h3>
        {badge != null && (
          <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: `${accent}1a`, color: accent }}>
            {badge}
          </span>
        )}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="text-center">
      <div className="text-2xl font-black text-gray-900 leading-none">{value}</div>
      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-1.5">{label}</div>
      {sub != null && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Pill({ children, color = 'gray' }) {
  const colors = {
    gray: 'bg-gray-100 text-gray-700',
    teal: 'bg-[#55C5D0]/10 text-[#3a9ba4]',
    green: 'bg-green-50 text-green-700',
    red: 'bg-red-50 text-red-600',
    pink: 'bg-pink-50 text-pink-700',
    blue: 'bg-blue-50 text-blue-700',
    amber: 'bg-amber-50 text-amber-700',
  };
  return <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${colors[color] || colors.gray}`}>{children}</span>;
}

function StatusChip({ active, activeLabel, inactiveLabel, color = 'green' }) {
  return active
    ? <Pill color={color}><CheckCircle className="w-3 h-3 inline mr-1 -mt-0.5" />{activeLabel}</Pill>
    : <Pill color="gray"><XCircle className="w-3 h-3 inline mr-1 -mt-0.5" />{inactiveLabel}</Pill>;
}

function Empty({ text }) {
  return <p className="text-sm text-gray-400 text-center py-2">{text}</p>;
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function ContactUnifiedProfile({ contactId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!contactId) return;
    setLoading(true);
    setError(null);
    getContactProfileSummary(contactId)
      .then((res) => setData(res.data))
      .catch((err) => {
        console.error('Error loading unified profile:', err);
        setError('Failed to load profile.');
      })
      .finally(() => setLoading(false));
  }, [contactId]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <Loader2 className="w-7 h-7 animate-spin mb-3" style={{ color: TEAL }} />
        <p className="text-sm font-bold">Building unified profile…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-red-400">
        <AlertCircle className="w-7 h-7 mb-3" />
        <p className="text-sm font-bold">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const {
    portal, property_profile: pp, searches, saves, suburbs,
    calculator: calc, chat, portfolio, finance, advocacy, landlord,
  } = data;

  return (
    <div className="space-y-6">
      {/* Identity banner */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-wrap items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-black" style={{ background: TEAL }}>
          {(data.contact?.name || '?').slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="font-bold text-gray-900">{data.contact?.name}</div>
          <div className="text-xs text-gray-400">{data.contact?.email}</div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {portal?.found
            ? <Pill color="teal"><Shield className="w-3 h-3 inline mr-1 -mt-0.5" />Portal account · joined {fmtDate(portal.date_joined)}</Pill>
            : <Pill color="gray">No portal account</Pill>}
          {pp?.urgency_score != null && (
            <Pill color={pp.urgency_score >= 60 ? 'red' : pp.urgency_score >= 30 ? 'amber' : 'gray'}>
              <Gauge className="w-3 h-3 inline mr-1 -mt-0.5" />Urgency {pp.urgency_score}/100
            </Pill>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Property Profile */}
        <Card icon={TrendingUp} title="Property Profile">
          {pp ? (
            <>
              <div className="grid grid-cols-3 gap-3 mb-5">
                <Stat label="Views" value={pp.views} />
                <Stat label="Saves" value={pp.saves} />
                <Stat label="Enquiries" value={pp.enquiries} />
                <Stat label="Offers" value={pp.offers} />
                <Stat label="Inspections" value={pp.inspections} />
                <Stat label="Gallery" value={pp.gallery_views} />
              </div>
              <div className="flex flex-wrap gap-2">
                {pp.investor_lean != null && (
                  <Pill color={pp.investor_lean >= 0.5 ? 'blue' : 'teal'}>
                    {pp.investor_lean >= 0.5 ? 'Investor lean' : 'Owner-occupier lean'} ({Math.round(pp.investor_lean * 100)}%)
                  </Pill>
                )}
                {pp.price_band?.p50 != null && (
                  <Pill color="green">Typical price {fmtMoney(pp.price_band.p50)}</Pill>
                )}
                {pp.bed_band?.mode != null && <Pill>{pp.bed_band.mode} bed focus</Pill>}
                {Object.entries(pp.property_type_mix || {})
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 2)
                  .map(([t, share]) => (
                    <Pill key={t}>{t} {Math.round(share * 100)}%</Pill>
                  ))}
              </div>
              {pp.recomputed_at && (
                <p className="text-[10px] text-gray-300 mt-3">Intent refreshed {fmtDate(pp.recomputed_at)}</p>
              )}
            </>
          ) : <Empty text="No portal behaviour to profile yet." />}
        </Card>

        {/* Status: Finance / Advocacy / Landlord */}
        <Card icon={Shield} title="Status">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-gray-500 flex items-center gap-2"><DollarSign className="w-4 h-4 text-gray-300" />Finance</span>
              <div className="flex items-center gap-2">
                <Pill color={finance?.is_finance_ready ? 'green' : 'gray'}>{finance?.label || 'Unknown'}</Pill>
                {finance?.source && (
                  <span className="text-[10px] text-gray-400 uppercase">{finance.source === 'agent' ? 'agent-set' : 'self-reported'}</span>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-gray-500 flex items-center gap-2"><Building2 className="w-4 h-4 text-gray-300" />Landlord / Investor</span>
              <div className="flex items-center gap-2">
                {landlord?.is_landlord && <Pill color="amber">Landlord</Pill>}
                {landlord?.is_investor && <Pill color="blue">Investor</Pill>}
                {!landlord?.is_landlord && !landlord?.is_investor && <Pill color="gray">Owner-occupier</Pill>}
              </div>
            </div>
          </div>
        </Card>

        {/* Searches */}
        <Card icon={Search} title="Searches" badge={searches?.total ?? 0}>
          {searches?.recent?.length ? (
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {searches.recent.map((s, i) => {
                const f = s.filters || {};
                // Pagination / sort metadata isn't a search criterion — hide it so
                // only the actual filters the buyer applied (suburb, price, beds…) show.
                const META_KEYS = new Set([
                  'page', 'page_size', 'pagesize', 'sort', 'ordering', 'order',
                  'count', 'offset', 'limit', 'total',
                ]);
                const LOW_SIGNAL = new Set(['', 'any', 'all']);
                const chips = Object.entries(f)
                  .filter(([k, v]) =>
                    !META_KEYS.has(k.toLowerCase()) &&
                    v != null &&
                    !(typeof v === 'string' && LOW_SIGNAL.has(v.toLowerCase())) &&
                    !(Array.isArray(v) && v.length === 0))
                  .slice(0, 5);
                return (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <span className="text-gray-300 shrink-0 w-20">{fmtDate(s.occurred_at)}</span>
                    <div className="flex flex-wrap items-center gap-1">
                      {chips.length
                        ? chips.map(([k, v]) => (
                            <span key={k} className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                              {k.replace(/_/g, ' ')}: {Array.isArray(v) ? v.join(', ') : String(v)}
                            </span>
                          ))
                        : <span className="text-gray-400 italic">Browsed all listings (no filters applied)</span>}
                      {s.count > 1 && (
                        <span className="px-1.5 py-0.5 bg-[#55C5D0]/10 text-[#3a9aa3] rounded font-semibold">
                          ×{s.count}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <Empty text="No searches recorded." />}
        </Card>

        {/* Saves */}
        <Card icon={Heart} title="Saved Properties" accent="#ec4899" badge={saves?.total ?? 0}>
          {saves?.items?.length ? (
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {saves.items.map((p) => (
                <a key={p.id} href={`/properties/${p.id}`} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors group">
                  <div className="w-8 h-8 rounded-lg bg-pink-50 flex items-center justify-center shrink-0">
                    <Home className="w-4 h-4 text-pink-300" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-gray-800 truncate">{p.display_address}</div>
                    <div className="text-[11px] text-gray-400">
                      {[p.suburb, p.state].filter(Boolean).join(', ')}{p.price ? ` · ${fmtMoney(p.price)}` : ''}
                    </div>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-gray-300 opacity-0 group-hover:opacity-100" />
                </a>
              ))}
            </div>
          ) : <Empty text="No saved properties." />}
        </Card>

        {/* Suburbs */}
        <Card icon={MapPin} title="Suburbs">
          <div className="space-y-4">
            <div>
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Stated preferences</div>
              {suburbs?.stated?.length
                ? <div className="flex flex-wrap gap-1.5">{suburbs.stated.map((s) => <Pill key={s} color="teal">{s}</Pill>)}</div>
                : <Empty text="None specified." />}
            </div>
            <div>
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Behaviour-derived (most engaged)</div>
              {suburbs?.behavioural?.length
                ? <div className="flex flex-wrap gap-1.5">
                    {suburbs.behavioural.slice(0, 8).map((s, i) => (
                      <Pill key={i} color="blue">
                        {s.suburb}{s.event_count ? ` · ${s.event_count}` : ''}
                      </Pill>
                    ))}
                  </div>
                : <Empty text="No browsing pattern yet." />}
            </div>
          </div>
        </Card>

        {/* Calculator usage */}
        <Card icon={Calculator} title="Calculator Usage" badge={calc?.total_runs ?? 0}>
          {calc?.runs?.length ? (
            <>
              <p className="text-[11px] text-gray-400 mb-3">Last used {fmtDate(calc.last_used_at)}</p>
              <div className="space-y-3 max-h-56 overflow-y-auto">
                {calc.runs.map((r, i) => (
                  <div key={i} className="space-y-1.5">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-gray-300 w-20 shrink-0">{fmtDate(r.occurred_at)}</span>
                      {r.property ? (
                        <a
                          href={`/properties/${r.property.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 min-w-0 font-bold text-gray-700 hover:text-[#55C5D0] transition-colors"
                        >
                          <Home className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                          <span className="truncate">{r.property.display_address}</span>
                          {[r.property.suburb, r.property.state].filter(Boolean).length > 0 && (
                            <span className="text-[11px] text-gray-400 font-normal truncate">
                              {[r.property.suburb, r.property.state].filter(Boolean).join(', ')}
                            </span>
                          )}
                        </a>
                      ) : (
                        <span
                          className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide"
                          style={{ background: 'var(--c-panel-warn)', color: 'var(--c-on-warn)', border: '1px solid var(--c-line-warn)' }}
                          title="Opened directly from the nav, not from a specific listing"
                        >
                          Opened directly · no listing
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs pl-20">
                      <Pill color={r.kind === 'mortgage' ? 'blue' : 'gray'}>
                        {r.kind === 'mortgage' ? 'Mortgage' : (r.mode || 'Investment')}
                      </Pill>
                      {r.price != null && <Pill color="green">{fmtMoney(r.price)}</Pill>}
                      {r.kind === 'mortgage' ? (
                        <>
                          {r.loan_amount != null && <Pill color="teal">{fmtMoney(r.loan_amount)} loan</Pill>}
                          {r.repayment != null && (
                            <Pill color="amber">{fmtMoney(r.repayment)}{r.frequency ? `/${r.frequency}` : ''}</Pill>
                          )}
                          {r.lvr_pct != null && <Pill>{Number(r.lvr_pct).toFixed(0)}% LVR</Pill>}
                          {r.rate_pct != null && <Pill>{Number(r.rate_pct).toFixed(2)}% rate</Pill>}
                        </>
                      ) : (
                        <>
                          {r.gross_yield_pct != null && <Pill color="teal">{Number(r.gross_yield_pct).toFixed(2)}% yield</Pill>}
                          {r.annual_cash_flow != null && (
                            <Pill color={r.annual_cash_flow >= 0 ? 'green' : 'red'}>{fmtMoney(r.annual_cash_flow)}/yr</Pill>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : <Empty text="Calculator not used yet." />}
        </Card>

        {/* Chat history */}
        <Card icon={Bot} title="Chat History" accent="#06b6d4" badge={chat?.sessions ?? 0}>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Sessions" value={chat?.sessions ?? 0} />
            <Stat label="Messages" value={chat?.messages ?? 0} />
            <Stat label="Last chat" value={chat?.last_at ? fmtDate(chat.last_at) : '—'} />
          </div>
          {!chat?.sessions && <p className="text-[11px] text-gray-300 text-center mt-3">See the Conversations tab for full transcripts.</p>}
        </Card>

        {/* Portfolio */}
        <Card icon={Briefcase} title="Portfolio" accent="#6366f1" badge={portfolio?.length ?? 0}>
          {portfolio?.length ? (
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {portfolio.map((p) => (
                <a key={p.id} href={`/properties/${p.id}`} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors group">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                    <Home className="w-4 h-4 text-indigo-300" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-gray-800 truncate">{p.display_address}</div>
                    <div className="text-[11px] text-gray-400">
                      {[p.suburb, p.state].filter(Boolean).join(', ')}{p.price ? ` · ${fmtMoney(p.price)}` : ''}
                    </div>
                  </div>
                  {p.is_sold && <Pill color="green">Owned</Pill>}
                  <ExternalLink className="w-3.5 h-3.5 text-gray-300 opacity-0 group-hover:opacity-100" />
                </a>
              ))}
            </div>
          ) : <Empty text="No purchased properties on record." />}
        </Card>
      </div>
    </div>
  );
}
